import {merge} from 'rxjs';
import {map, filter, scan, shareReplay, throttleTime, withLatestFrom, startWith} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {contractsMarkSymbolCache$, contractsIndexSymbolCache$} from '@/config';
import {tickMsg$} from '@/raw/tick';
import {contractMarket24h$} from './market24h-contract';
import type {ws, ContractMarket, ContractPair} from '@/types';

type TickMsg = ws.TickMsg;
type CacheX = {changed: boolean; cache: Map<string, ContractMarket>};
type Action = (c: CacheX) => CacheX;

const newContractMarketAction$ = contractMarket24h$.pipe(map(convertNewContractMarketAction));

const updatePriceAction$ = tickMsg$.pipe(
  withLatestFrom(contractsMarkSymbolCache$, contractsIndexSymbolCache$),
  map(convertUpdatePriceAction)
);

export const contractMarketRealtimeCache$ = merge<Action[]>(newContractMarketAction$, updatePriceAction$).pipe(
  filter(Boolean),
  scan((cacheX: CacheX, action: Action) => action(cacheX), {changed: true, cache: new Map<string, ContractMarket>()}),
  filter((cacheX: CacheX) => cacheX.changed),
  map((cacheX: CacheX) => cacheX.cache),
  throttleTime(1000),
  startWith(new Map<string, ContractMarket>()),
  shareReplay(1)
);

function convertNewContractMarketAction(markets: ContractMarket[]): Action {
  return function (cacheX: CacheX) {
    cacheX.changed = true;
    markets.forEach((market: ContractMarket) => cacheX.cache.set(market.symbol, market));
    return cacheX;
  };
}

function convertUpdatePriceAction([tickMsg, contractsMarkSymbolCache$, contractsIndexSymbolCache$]: [
  TickMsg,
  Map<string, ContractPair>,
  Map<string, ContractPair>
]): Action {
  const {type, contract} = findContractInfo(contractsMarkSymbolCache$, contractsIndexSymbolCache$, tickMsg.tick.symbol);
  if (type === 'None') {
    return null;
  }

  return function (cacheX: CacheX) {
    cacheX.changed = false;
    const {cache} = cacheX;
    const {symbol} = contract;
    if (cache.has(symbol)) {
      const oldData = cache.get(symbol);
      const {last, scale} = tickMsg.tick;
      const priceEp = last * Math.pow(10, 4 - scale);
      const {pricePrecision, priceScale} = contract;
      const price = numUtil.delimit(numUtil.floor(priceEp, pricePrecision, priceScale));
      if (type === 'MarkPrice' && oldData.markPriceEp !== priceEp) {
        cacheX.changed = true;
        const newData: ContractMarket = {...oldData, markPriceEp: priceEp, markPrice: price};
        cache.set(symbol, newData);
      }
      if (type === 'IndexPrice' && oldData.indexPriceEp !== priceEp) {
        cacheX.changed = true;
        const newData: ContractMarket = {...oldData, indexPriceEp: priceEp, indexPrice: price};
        cache.set(symbol, newData);
      }
    }
    return cacheX;
  };
}

function findContractInfo(
  contractsMarkSymbolCache: Map<string, ContractPair>,
  contractsIndexSymbolCache: Map<string, ContractPair>,
  symbol: string
) {
  const contractByMarkPrice = contractsMarkSymbolCache.get(symbol);
  if (contractByMarkPrice) {
    return {type: 'MarkPrice', contract: contractByMarkPrice};
  }

  const contractByIndexPrice = contractsIndexSymbolCache.get(symbol);
  if (contractByIndexPrice) {
    return {type: 'IndexPrice', contract: contractByIndexPrice};
  }

  return {type: 'None', contract: null};
}
